import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { downloadCSV } from '../../../utils/exportCsv';
import { getPaidUsersReport, getPaidUsersList } from "../../../actions/adminReportActions";
import Loader from "../../Loader";
import "./PaidUsersReport.css";
import CurrencyToggle from '../../Common/CurrencyToggle';
import { getPlanChartLabel } from '../../../utils/subscriptionPlanLabel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PaidUsersReport = () => {
  const dispatch = useDispatch();
  const { report, paidUsersList, loading, listLoading, pagination } = useSelector(
    (state) => state.adminReport
  );

  const [dateRange, setDateRange] = useState("last30days");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState("subscribedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currencyUnit, setCurrencyUnit] = useState(() => { try { return localStorage.getItem('reports_currency_unit') || 'NGN'; } catch(e) { return 'NGN'; } });

  // Fetch report data
  useEffect(() => {
    if (dateRange === "custom") {
      if (customStartDate && customEndDate) {
        dispatch(
          getPaidUsersReport(
            "custom",
            customStartDate,
            customEndDate
          )
        );
      }
    } else {
      dispatch(getPaidUsersReport(dateRange));
    }
  }, [dispatch, dateRange, customStartDate, customEndDate]);

  // Fetch users list
  useEffect(() => {
    if (dateRange === "custom") {
      if (customStartDate && customEndDate) {
        dispatch(
          getPaidUsersList(page, limit, "custom", customStartDate, customEndDate, sortBy, sortOrder)
        );
      }
    } else {
      dispatch(getPaidUsersList(page, limit, dateRange, null, null, sortBy, sortOrder));
    }
  }, [dispatch, page, limit, dateRange, customStartDate, customEndDate, sortBy, sortOrder]);

  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
    setPage(1);
  };

  const handleCustomDateChange = (e, type) => {
    if (type === "start") {
      setCustomStartDate(e.target.value);
    } else {
      setCustomEndDate(e.target.value);
    }
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  // Prepare chart data
  const timeSeriesData =
    report?.timeSeries?.paidUsersOverTime || [];
  const topPlans = report?.details?.topPlans || [];
  const revenueByType = report?.details?.revenueByType || [];
  const summary = report?.summary || {};
  const breakdown = report?.breakdown || {};

  // Line chart for users over time
  const usersTimeSeriesChartData = {
    labels: timeSeriesData.map((d) => d.date),
    datasets: [
      {
        label: "Paid Users",
        data: timeSeriesData.map((d) => d.users),
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Bar chart for top plans
  const topPlansChartData = {
    labels: topPlans.map((p) => getPlanChartLabel(p)),
    datasets: [
      {
        label: "Subscribers",
        data: topPlans.map((p) => p.subscribers),
        backgroundColor: "#2196F3",
      },
    ],
  };

  // Doughnut chart for subscription status (filter out zero slices and add percentages)
  const rawStatus = breakdown.subscriptionStatus || { active: 0, expired: 0, cancelled: 0, pending: 0 };
  const statusMap = [
    { key: 'active', label: 'Active', color: '#4CAF50' },
    { key: 'expired', label: 'Expired', color: '#FF9800' },
    { key: 'cancelled', label: 'Cancelled', color: '#F44336' },
    { key: 'pending', label: 'Pending', color: '#9C27B0' },
  ];

  const statusLabels = [];
  const statusValues = [];
  const statusColors = [];

  statusMap.forEach((s) => {
    const v = Number(rawStatus[s.key] || 0);
    if (v > 0) {
      statusLabels.push(s.label);
      statusValues.push(v);
      statusColors.push(s.color);
    }
  });

  // If all values are zero, show a placeholder slice to avoid a full-single-color chart
  if (statusValues.length === 0) {
    statusLabels.push('No Data');
    statusValues.push(1);
    statusColors.push('#E0E0E0');
  }

  const statusPieChartData = {
    labels: statusLabels,
    datasets: [
      {
        data: statusValues,
        backgroundColor: statusColors,
      },
    ],
  };

  const statusChartOptions = {
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        display: true,
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: ${value} (${pct}%)`;
          }
        }
      }
    }
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Paid Users Report
            <small>Impact Analytics Dashboard</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Paid Users Report</li>
          </ol>
        </section>

        <section className="content">
          {/* Date Range Filter */}
          <div className="row">
            <div className="col-md-12">
              <div className="box box-info">
                <div className="box-header with-border">
                  <h3 className="box-title">Filter Report</h3>
                </div>
                <div className="box-body">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Date Range</label>
                        <select
                          className="form-control"
                          value={dateRange}
                          onChange={handleDateRangeChange}
                        >
                          <option value="last7days">Last 7 Days</option>
                          <option value="last30days">Last 30 Days</option>
                          <option value="last90days">Last 90 Days</option>
                          <option value="ytd">Year to Date</option>
                          <option value="custom">Custom Range</option>
                        </select>
                      </div>
                    </div>

                    {dateRange === "custom" && (
                      <>
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>Start Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={customStartDate}
                              onChange={(e) => handleCustomDateChange(e, "start")}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>End Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={customEndDate}
                              onChange={(e) => handleCustomDateChange(e, "end")}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row">
            <div className="col-md-3">
              <div className="info-box">
                <span className="info-box-icon bg-green">
                  <i className="fa fa-users"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">Total Paid Users</span>
                  <span className="info-box-number">
                    {loading ? <Loader /> : summary.totalPaidUsers || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="info-box">
                <span className="info-box-icon bg-blue">
                  <i className="fa fa-user-check"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">Unique Users</span>
                  <span className="info-box-number">
                    {loading ? <Loader /> : summary.uniqueUsers || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="info-box">
                <span className="info-box-icon bg-yellow">
                  <i className="fa fa-money"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">Total Revenue</span>
                  <span className="info-box-number">
                    {loading ? <Loader /> : (currencyUnit === 'KOBO' ? `${Number(summary.totalRevenue || 0 * 100).toLocaleString()} kobo` : `₦${Number(summary.totalRevenue || 0).toLocaleString()}`)}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="info-box">
                <span className="info-box-icon bg-red">
                  <i className="fa fa-pie-chart"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">Avg Revenue/User</span>
                  <span className="info-box-number">
                    {loading ? <Loader /> : (currencyUnit === 'KOBO' ? `${Number(summary.averageRevenuePerUser || 0 * 100).toLocaleString()} kobo` : `₦${Number(summary.averageRevenuePerUser || 0).toLocaleString()}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="row">
            {/* Paid Users Over Time */}
            <div className="col-md-8">
              <div className="box box-primary">
                <div className="box-header with-border">
                  <h3 className="box-title">Paid Users Over Time</h3>
                </div>
                <div className="box-body">
                  {loading ? (
                    <Loader />
                  ) : (timeSeriesData.length > 0 && timeSeriesData.some(d => d.users > 0)) ? (
                    <div style={{height:420}}>
                      <Line
                        data={usersTimeSeriesChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: true, position: "top" },
                          },
                          scales: { y: { beginAtZero: true } }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-center">No paid users time-series data for the selected range.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Subscription Status Breakdown */}
            <div className="col-md-4">
              <div className="box box-info">
                <div className="box-header with-border">
                  <h3 className="box-title">Subscription Status</h3>
                </div>
                <div className="box-body">
                  {loading ? (
                    <Loader />
                  ) : (
                    <div className="chart-container" style={{ height: 320 }}>
                      <Doughnut data={statusPieChartData} options={statusChartOptions} />
                    </div>
                  )}
                </div>
                <div className="box-footer">
                  <div style={{float:'right'}}>
                    <button className="btn btn-default btn-sm" onClick={() => downloadCSV('subscription_status.csv', Object.keys(rawStatus).map(k => ({ status: k, count: rawStatus[k] })))}>Export CSV</button>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <p>
                        <strong>Active:</strong> {Number(rawStatus.active || 0)}
                      </p>
                      <p>
                        <strong>Expired:</strong> {Number(rawStatus.expired || 0)}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Cancelled:</strong> {Number(rawStatus.cancelled || 0)}
                      </p>
                      <p>
                        <strong>Pending:</strong> {Number(rawStatus.pending || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
                          <div className="col-md-4" style={{display:'flex', alignItems:'center', justifyContent:'flex-end'}}>
                            <CurrencyToggle onChange={(u) => setCurrencyUnit(u)} />
                          </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="row">
            {/* Top Subscription Plans */}
            <div className="col-md-6">
              <div className="box box-success">
                <div className="box-header with-border">
                  <h3 className="box-title">Top Subscription Plans</h3>
                </div>
                <div className="box-body">
                  {loading ? (
                    <Loader />
                  ) : (topPlans.length > 0 && topPlans.some(p => p.subscribers > 0)) ? (
                    <div style={{height:360}}>
                      <Bar
                        data={topPlansChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: "y",
                          plugins: { legend: { display: true, position: "top" } },
                          scales: { x: { beginAtZero: true } }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-center">No top-plans data for the selected range.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Conversion Rate & Other Metrics */}
            <div className="col-md-6">
              <div className="box box-warning">
                <div className="box-header with-border">
                  <h3 className="box-title">Key Metrics</h3>
                </div>
                <div className="box-body">
                  {loading ? (
                    <Loader />
                  ) : (
                    <div className="metrics-list">
                      <div className="metric-item">
                        <span className="metric-label">Conversion Rate (Free to Paid)</span>
                        <span className="metric-value">
                          {breakdown.conversionRate || "0%"}
                        </span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Reporting Period</span>
                        <span className="metric-value">
                          {report?.dateRange?.label || "N/A"}
                        </span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Total Subscriptions</span>
                        <span className="metric-value">
                          {summary.totalPaidUsers || 0}
                        </span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Revenue Per Day (Avg)</span>
                        <span className="metric-value">
                          {currencyUnit === 'KOBO' ? `${(Number(summary.totalRevenue || 0) / (report?.dateRange?.label === "Last 7 Days" ? 7 : 30) * 100).toLocaleString()} kobo` : `₦${(Number(summary.totalRevenue || 0) / (report?.dateRange?.label === "Last 7 Days" ? 7 : 30)).toLocaleString()}`}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Paid Users List */}
          <div className="row">
            <div className="col-md-12">
              <div className="box box-primary">
                <div className="box-header with-border">
                  <h3 className="box-title">Detailed Paid Users List</h3>
                </div>
                <div className="box-body">
                  {listLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-striped table-hover">
                          <thead>
                            <tr>
                              <th
                                onClick={() => handleSortChange("user")}
                                style={{ cursor: "pointer" }}
                              >
                                Email {sortBy === "user" && <i className={`fa fa-arrow-${sortOrder === "asc" ? "up" : "down"}`}></i>}
                              </th>
                              <th>First Name</th>
                              <th>Last Name</th>
                              <th
                                onClick={() => handleSortChange("subscribedAt")}
                                style={{ cursor: "pointer" }}
                              >
                                Subscription Date {sortBy === "subscribedAt" && <i className={`fa fa-arrow-${sortOrder === "asc" ? "up" : "down"}`}></i>}
                              </th>
                              <th>Plan</th>
                              <th>Price</th>
                              <th>Duration</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paidUsersList.length > 0 ? (
                              paidUsersList.map((user, index) => (
                                <tr key={index}>
                                  <td>{user.user?.email || "N/A"}</td>
                                  <td>{user.user?.first_name || "N/A"}</td>
                                  <td>{user.user?.last_name || "N/A"}</td>
                                  <td>
                                    {new Date(user.subscribedAt).toLocaleDateString()}
                                  </td>
                                  <td>{user.planName || "N/A"}</td>
                                  <td>{currencyUnit === 'KOBO' ? `${Number(user.price || 0).toLocaleString()} kobo` : `₦${(Number(user.price || 0) / 100).toLocaleString()}`}</td>
                                  <td>{user.duration} days</td>
                                  <td>
                                    <span
                                      className={`label ${
                                        user.status
                                          ? "label-success"
                                          : "label-danger"
                                      }`}
                                    >
                                      {user.status ? "Active" : "Inactive"}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center">
                                  No paid users found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {pagination && (
                        <nav className="pagination-nav">
                          <ul className="pagination">
                            <li
                              className={page === 1 ? "disabled" : ""}
                            >
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (page > 1) handlePageChange(page - 1);
                                }}
                              >
                                Previous
                              </a>
                            </li>

                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                              <li
                                key={p}
                                className={page === p ? "active" : ""}
                              >
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(p);
                                  }}
                                >
                                  {p}
                                </a>
                              </li>
                            ))}

                            <li
                              className={page === pagination.totalPages ? "disabled" : ""}
                            >
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (page < pagination.totalPages)
                                    handlePageChange(page + 1);
                                }}
                              >
                                Next
                              </a>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default PaidUsersReport;
